import React, { useState } from 'react';
import { FaTimes, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';

interface BatchRemoveDocumentsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRemove: (documentIds: string[]) => Promise<void>;
}

const BatchRemoveDocumentsModal: React.FC<BatchRemoveDocumentsModalProps> = ({ isOpen, onClose, onRemove }) => {
    const [input, setInput] = useState('');
    const [isRemoving, setIsRemoving] = useState(false);

    const nfeList = input.split('\n').map(s => s.trim()).filter(Boolean);

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text');
        const items = pasted.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
        setInput(prev => prev ? `${prev}\n${items.join('\n')}` : items.join('\n'));
    };

    const handleRemove = async () => {
        if (nfeList.length === 0) {
            toast.error('Informe ao menos um número NFe');
            return;
        }
        setIsRemoving(true);
        try {
            await onRemove(nfeList);
            setInput('');
            onClose();
        } finally {
            setIsRemoving(false);
        }
    };

    const handleClose = () => {
        setInput('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg w-full max-w-lg flex flex-col">
                <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold dark:text-white">Remover Documentos</h3>
                    <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                <div className="p-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Informe os números NFe (um por linha)
                    </label>
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onPaste={handlePaste}
                        placeholder="NFe52260600117842001795550010000132181872574022&#10;NFe52260600117842001795550010000132191851289911"
                        rows={10}
                        className="w-full text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-slate-700 border border-gray-300 dark:border-gray-600 rounded p-3 focus:outline-none focus:border-blue-500 dark:focus:border-blue-400 font-mono resize-none"
                    />
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {nfeList.length} item(ns) na lista
                    </p>
                </div>

                <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
                    <button onClick={handleClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 rounded text-sm">
                        Cancelar
                    </button>
                    <button
                        onClick={handleRemove}
                        disabled={isRemoving || nfeList.length === 0}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FaTrash /> {isRemoving ? 'Removendo...' : `Remover (${nfeList.length})`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BatchRemoveDocumentsModal;
